import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "searchInput",
    "fileList", 
    "treeView",
    "listView",
    "viewModeToggle",
    "fileInput"
  ]
  static values = {
    sessionId: String,
    viewMode: { type: String, default: "tree" }
  }

  connect() {
    console.log('Claude Code File Browser Controller v2.0 - Images & PDF support enabled')
    this.initializeViewMode()
    this.setupSearchFilter()
  }

  // View mode management
  initializeViewMode() {
    this.updateViewMode()
    this.updateViewModeToggle()
  }

  toggleViewMode() {
    this.viewModeValue = this.viewModeValue === "tree" ? "list" : "tree"
    this.updateViewMode()
    this.updateViewModeToggle()
  }

  updateViewMode() {
    if (this.hasTreeViewTarget && this.hasListViewTarget) {
      if (this.viewModeValue === "tree") {
        this.treeViewTarget.classList.remove("hidden")
        this.listViewTarget.classList.add("hidden")
      } else {
        this.treeViewTarget.classList.add("hidden")
        this.listViewTarget.classList.remove("hidden")
      }
    }
  }

  updateViewModeToggle() {
    if (this.hasViewModeToggleTarget) {
      const icon = this.viewModeToggleTarget.querySelector("i")
      if (icon) {
        // When in tree view, show list icon (to switch to list view)
        // When in list view, show tree icon (to switch to tree view)
        icon.className = this.viewModeValue === "tree" ? "fas fa-list" : "fas fa-folder-tree"
      }
    }
  }

  // Directory management (tree view)
  toggleDirectory(event) {
    const directory = event.currentTarget.dataset.directory
    const iconTarget = `directoryIcon${directory.replace(/\//g, '_')}`
    const contentTarget = `directoryContent${directory.replace(/\//g, '_')}`
    
    const iconElement = this.element.querySelector(`[data-claude-code-file-browser-target="${iconTarget}"]`)
    const contentElement = this.element.querySelector(`[data-claude-code-file-browser-target="${contentTarget}"]`)
    
    if (contentElement) {
      const isExpanded = !contentElement.classList.contains("hidden")
      
      if (isExpanded) {
        contentElement.classList.add("hidden")
        if (iconElement) {
          iconElement.classList.remove("rotate-90")
        }
      } else {
        contentElement.classList.remove("hidden")
        if (iconElement) {
          iconElement.classList.add("rotate-90")
        }
      }
    }
  }

  // File context management
  toggleFileContext(event) {
    const checkbox = event.currentTarget
    const filePath = checkbox.dataset.filePath
    const inContext = checkbox.checked

    this.updateFileContextStatus(filePath, inContext)
    this.broadcastContextChange(filePath, inContext)
  }

  toggleAllFiles(event) {
    const selectAll = event.currentTarget.checked
    const fileCheckboxes = this.element.querySelectorAll('input[type="checkbox"][data-file-path]')
    
    fileCheckboxes.forEach(checkbox => {
      if (checkbox.checked !== selectAll) {
        checkbox.checked = selectAll
        this.updateFileContextStatus(checkbox.dataset.filePath, selectAll)
      }
    })

    if (fileCheckboxes.length > 0) {
      this.broadcastBulkContextChange(selectAll)
    }
  }

  updateFileContextStatus(filePath, inContext) {
    // Update visual indicators for the file
    const fileItems = this.element.querySelectorAll(`[data-file-path="${filePath}"]`)
    fileItems.forEach(item => {
      item.dataset.inContext = inContext
      
      if (inContext) {
        item.classList.add("bg-success/10")
        
        // Add "In Context" badge if not present
        const badge = item.querySelector(".badge-success")
        if (!badge) {
          const nameElement = item.querySelector(".font-mono")
          if (nameElement && nameElement.parentNode) {
            const contextBadge = document.createElement("span")
            contextBadge.className = "badge badge-xs badge-success"
            contextBadge.textContent = "In Context"
            nameElement.parentNode.appendChild(contextBadge)
          }
        }
      } else {
        item.classList.remove("bg-success/10")
        
        // Remove "In Context" badge
        const badge = item.querySelector(".badge-success")
        if (badge) {
          badge.remove()
        }
      }
    })

    this.updateContextSummary()
  }

  // Context management actions
  clearContext() {
    const fileCheckboxes = this.element.querySelectorAll('input[type="checkbox"][data-file-path]')
    
    fileCheckboxes.forEach(checkbox => {
      if (checkbox.checked) {
        checkbox.checked = false
        this.updateFileContextStatus(checkbox.dataset.filePath, false)
      }
    })

    this.broadcastContextClear()
  }

  addRecentFiles() {
    const recentFileItems = this.element.querySelectorAll('[data-recently-modified="true"]')
    
    recentFileItems.forEach(item => {
      const checkbox = item.querySelector('input[type="checkbox"]')
      if (checkbox && !checkbox.checked) {
        checkbox.checked = true
        this.updateFileContextStatus(checkbox.dataset.filePath, true)
      }
    })

    this.broadcastRecentFilesAdded()
  }

  addAllFiles() {
    const fileCheckboxes = this.element.querySelectorAll('input[type="checkbox"][data-file-path]')
    
    fileCheckboxes.forEach(checkbox => {
      if (!checkbox.checked) {
        checkbox.checked = true
        this.updateFileContextStatus(checkbox.dataset.filePath, true)
      }
    })

    this.broadcastAllFilesAdded()
  }

  // Project document context methods
  addProjectPRD() {
    this.broadcastProjectDocumentAdded('prd')
  }

  addProjectTasks() {
    this.broadcastProjectDocumentAdded('tasks')
  }

  addClaudeMD() {
    this.broadcastProjectDocumentAdded('claude_md')
  }

  // Server log methods
  addDockerLogs() {
    this.broadcastServerLogAdded('docker')
  }

  addRailsLogs() {
    this.broadcastServerLogAdded('rails')
  }

  addSidekiqLogs() {
    this.broadcastServerLogAdded('sidekiq')
  }

  // File upload functionality
  uploadFile() {
    // Trigger the hidden file input
    this.fileInputTarget.click()
  }

  handleFileSelection(event) {
    const files = event.target.files
    if (files.length === 0) return

    // Process each selected file
    Array.from(files).forEach(file => {
      this.processUploadedFile(file)
    })

    // Clear the input so the same file can be selected again
    event.target.value = ''
  }

  processUploadedFile(file) {
    console.log(`Processing file: ${file.name}, size: ${file.size}, type: ${file.type}`)
    
    // Validate file size (limit to 10MB)
    const maxSize = 10 * 1024 * 1024 // 10MB
    if (file.size > maxSize) {
      console.log(`File too large: ${file.size} > ${maxSize}`)
      this.showUploadError(`File "${file.name}" is too large. Maximum size is 10MB.`)
      return
    }

    // Validate file type
    if (!this.isAllowedFileType(file)) {
      console.log(`File validation failed for: ${file.name}, type: ${file.type}`)
      this.showUploadError(`File type "${file.type}" is not allowed. Please upload text, image, or PDF files only.`)
      return
    }

    // Determine how to read the file based on type
    if (this.isImageFile(file)) {
      console.log(`Reading as image file: ${file.name}`)
      this.readImageFile(file)
    } else if (this.isPDFFile(file)) {
      console.log(`Reading as PDF file: ${file.name}`)
      this.readBinaryFile(file)
    } else {
      console.log(`Reading as text file: ${file.name}`)
      this.readTextFile(file)
    }
  }

  readTextFile(file) {
    const reader = new FileReader()
    reader.onload = (e) => {
      const content = e.target.result
      this.uploadFileToSession(file.name, content, file.type, file.size)
    }
    reader.onerror = () => {
      this.showUploadError(`Failed to read file "${file.name}".`)
    }
    reader.readAsText(file)
  }

  readImageFile(file) {
    console.log(`Starting to read image file: ${file.name}`)
    const reader = new FileReader()
    reader.onload = (e) => {
      console.log(`Image file read successfully: ${file.name}, data length: ${e.target.result.length}`)
      const content = e.target.result // This will be a data URL (base64)
      this.uploadFileToSession(file.name, content, file.type, file.size)
    }
    reader.onerror = (e) => {
      console.error(`Failed to read image: ${file.name}`, e)
      this.showUploadError(`Failed to read image "${file.name}".`)
    }
    reader.readAsDataURL(file)
  }

  readBinaryFile(file) {
    const reader = new FileReader()
    reader.onload = (e) => {
      const content = e.target.result // This will be a data URL (base64)
      this.uploadFileToSession(file.name, content, file.type, file.size)
    }
    reader.onerror = () => {
      this.showUploadError(`Failed to read file "${file.name}".`)
    }
    reader.readAsDataURL(file)
  }

  isAllowedFileType(file) {
    console.log(`Checking file type for: ${file.name}, MIME type: ${file.type}`)
    
    // Allow text-based files, images, and PDFs
    const allowedTypes = [
      // Text files
      'text/plain',
      'text/markdown',
      'application/json',
      'text/javascript',
      'application/javascript',
      'text/css',
      'text/html',
      'text/xml',
      'application/xml',
      'text/yaml',
      'application/x-yaml',
      'text/x-python',
      'text/x-ruby',
      'text/x-php',
      'text/x-java-source',
      'text/x-c',
      'text/x-cpp',
      'text/x-csharp',
      'text/x-go',
      'text/x-rust',
      'text/x-swift',
      'text/x-kotlin',
      'text/x-scala',
      // Image files
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/gif',
      'image/webp',
      'image/svg+xml',
      // PDF files
      'application/pdf'
    ]

    // Check by MIME type
    if (allowedTypes.includes(file.type)) {
      console.log(`File type ${file.type} is allowed (MIME type match)`)
      return true
    }

    // Check by file extension for files without proper MIME types
    const allowedExtensions = [
      // Text files
      '.txt', '.md', '.json', '.js', '.ts', '.jsx', '.tsx', 
      '.py', '.rb', '.php', '.java', '.c', '.cpp', '.h', '.hpp',
      '.cs', '.go', '.rs', '.swift', '.kt', '.scala', '.clj',
      '.hs', '.ml', '.fs', '.elm', '.dart', '.lua', '.sh',
      '.bash', '.zsh', '.fish', '.ps1', '.bat', '.cmd',
      '.yml', '.yaml', '.cfg', '.ini', '.toml', '.conf',
      '.env', '.gitignore', '.dockerfile', '.makefile',
      '.gemfile', '.requirements.txt',
      // Image files
      '.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg',
      // PDF files
      '.pdf'
    ]

    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'))
    const extensionAllowed = allowedExtensions.includes(fileExtension)
    console.log(`File extension ${fileExtension} ${extensionAllowed ? 'is' : 'is not'} allowed`)
    return extensionAllowed
  }

  isImageFile(file) {
    const imageTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/svg+xml']
    const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg']
    
    if (imageTypes.includes(file.type)) {
      return true
    }
    
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'))
    return imageExtensions.includes(fileExtension)
  }

  isPDFFile(file) {
    return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
  }

  uploadFileToSession(fileName, content, fileType, fileSize) {
    console.log(`Uploading file to session: ${fileName}, type: ${fileType}, size: ${fileSize}`)
    this.broadcastFileUpload({
      fileName: fileName,
      content: content,
      fileType: fileType,
      fileSize: fileSize
    })
  }

  showUploadError(message) {
    // Create a temporary error alert
    const alert = document.createElement('div')
    alert.className = 'alert alert-error fixed top-4 right-4 z-50 w-auto max-w-md shadow-lg'
    alert.innerHTML = `
      <div class="flex items-center">
        <i class="fas fa-exclamation-triangle mr-2"></i>
        <span>${message}</span>
        <button class="btn btn-sm btn-ghost ml-2" onclick="this.parentElement.parentElement.remove()">
          <i class="fas fa-times"></i>
        </button>
      </div>
    `
    
    document.body.appendChild(alert)
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (alert.parentElement) {
        alert.remove()
      }
    }, 5000)
  }

  // Project document context management
  toggleProjectDocumentContext(event) {
    const checkbox = event.currentTarget
    const documentType = checkbox.dataset.documentType
    const inContext = checkbox.checked

    this.updateProjectDocumentContextStatus(documentType, inContext)
    this.broadcastProjectDocumentContextChange(documentType, inContext)
  }

  removeProjectDocument(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const documentType = event.currentTarget.dataset.documentType
    this.broadcastProjectDocumentRemoved(documentType)
  }

  updateProjectDocumentContextStatus(documentType, inContext) {
    const documentItem = this.element.querySelector(`[data-document-type="${documentType}"]`)
    if (documentItem) {
      documentItem.dataset.inContext = inContext
      
      if (inContext) {
        documentItem.classList.add('bg-success/5', 'border-l-2', 'border-success')
      } else {
        documentItem.classList.remove('bg-success/5', 'border-l-2', 'border-success')
      }
    }
  }

  // Uploaded file context management
  toggleUploadedFileContext(event) {
    const checkbox = event.currentTarget
    const fileId = checkbox.dataset.fileId
    const inContext = checkbox.checked

    this.updateUploadedFileContextStatus(fileId, inContext)
    this.broadcastUploadedFileContextChange(fileId, inContext)
  }

  removeUploadedFile(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const fileId = event.currentTarget.dataset.fileId
    this.broadcastUploadedFileRemoved(fileId)
  }

  updateUploadedFileContextStatus(fileId, inContext) {
    const fileItem = this.element.querySelector(`[data-file-id="${fileId}"]`)
    if (fileItem) {
      fileItem.dataset.inContext = inContext
      
      if (inContext) {
        fileItem.classList.add('bg-success/5', 'border-l-2', 'border-success')
      } else {
        fileItem.classList.remove('bg-success/5', 'border-l-2', 'border-success')
      }
    }
  }

  // File operations
  previewFile(event) {
    const filePath = event.currentTarget.dataset.filePath
    this.broadcastFilePreview(filePath)
  }

  openFile(event) {
    const filePath = event.currentTarget.dataset.filePath
    this.broadcastFileOpen(filePath)
  }

  // Search functionality
  setupSearchFilter() {
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.addEventListener("input", this.filterFiles.bind(this))
    }
  }

  filterFiles(event) {
    const searchTerm = event.target.value.toLowerCase()
    const fileItems = this.element.querySelectorAll('.file-item, tbody tr[data-file-path]')
    
    fileItems.forEach(item => {
      const filePath = item.dataset.filePath || ""
      const fileName = filePath.split("/").pop() || ""
      
      const matchesSearch = fileName.toLowerCase().includes(searchTerm) || 
                          filePath.toLowerCase().includes(searchTerm)
      
      if (matchesSearch || searchTerm === "") {
        item.style.display = ""
      } else {
        item.style.display = "none"
      }
    })

    // Also filter directory headers in tree view
    if (this.hasTreeViewTarget) {
      const directoryHeaders = this.treeViewTarget.querySelectorAll('.file-directory')
      directoryHeaders.forEach(dir => {
        const visibleFiles = dir.querySelectorAll('.file-item:not([style*="display: none"])')
        if (visibleFiles.length > 0 || searchTerm === "") {
          dir.style.display = ""
        } else {
          dir.style.display = "none"
        }
      })
    }
  }

  // Context summary updates
  updateContextSummary() {
    const contextFiles = this.element.querySelectorAll('input[type="checkbox"][data-file-path]:checked')
    const contextCount = contextFiles.length
    
    // Update context count displays
    const contextCountElements = this.element.querySelectorAll('[data-context-count]')
    contextCountElements.forEach(element => {
      element.textContent = contextCount
    })

    // Update context summary bar visibility
    const contextSummary = this.element.querySelector('.bg-info\\/10')
    if (contextSummary) {
      if (contextCount > 0) {
        contextSummary.style.display = ""
      } else {
        contextSummary.style.display = "none"
      }
    }
  }

  // ActionCable broadcasting methods
  broadcastContextChange(filePath, inContext) {
    this.broadcast("update_context", {
      action: inContext ? 'add_file' : 'remove_file',
      file_path: filePath,
      session_id: this.sessionIdValue
    })
  }

  broadcastBulkContextChange(selectAll) {
    this.broadcast("update_context", {
      action: selectAll ? 'add_all_files' : 'clear',
      session_id: this.sessionIdValue
    })
  }

  broadcastContextClear() {
    this.broadcast("update_context", {
      action: 'clear',
      session_id: this.sessionIdValue
    })
  }

  broadcastRecentFilesAdded() {
    this.broadcast("add_recent_files_to_context", {
      session_id: this.sessionIdValue
    })
  }

  broadcastAllFilesAdded() {
    this.broadcast("add_all_files_to_context", {
      session_id: this.sessionIdValue
    })
  }

  broadcastFilePreview(filePath) {
    this.broadcast("preview_file", {
      file_path: filePath,
      session_id: this.sessionIdValue
    })
  }

  broadcastFileOpen(filePath) {
    this.broadcast("open_file", {
      file_path: filePath,
      session_id: this.sessionIdValue
    })
  }

  broadcastProjectDocumentAdded(documentType) {
    this.broadcast("add_project_document", {
      document_type: documentType,
      session_id: this.sessionIdValue
    })
  }

  broadcastServerLogAdded(logType) {
    this.broadcast("add_server_log", {
      log_type: logType,
      session_id: this.sessionIdValue
    })
  }

  broadcastProjectDocumentContextChange(documentType, inContext) {
    this.broadcast("update_project_document_context", {
      document_type: documentType,
      in_context: inContext,
      session_id: this.sessionIdValue
    })
  }

  broadcastProjectDocumentRemoved(documentType) {
    this.broadcast("remove_project_document", {
      document_type: documentType,
      session_id: this.sessionIdValue
    })
  }

  broadcastFileUpload(fileData) {
    console.log(`Broadcasting file upload: ${fileData.fileName}`)
    this.broadcast("upload_file", {
      file_name: fileData.fileName,
      content: fileData.content,
      file_type: fileData.fileType,
      file_size: fileData.fileSize,
      session_id: this.sessionIdValue
    })
  }

  broadcastUploadedFileContextChange(fileId, inContext) {
    this.broadcast("update_uploaded_file_context", {
      file_id: fileId,
      in_context: inContext,
      session_id: this.sessionIdValue
    })
  }

  broadcastUploadedFileRemoved(fileId) {
    this.broadcast("remove_uploaded_file", {
      file_id: fileId,
      session_id: this.sessionIdValue
    })
  }

  // Server log context management
  toggleServerLogContext(event) {
    const checkbox = event.currentTarget
    const logType = checkbox.dataset.logType
    const inContext = checkbox.checked

    this.updateServerLogContextStatus(logType, inContext)
    this.broadcastServerLogContextChange(logType, inContext)
  }

  refreshServerLog(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const logType = event.currentTarget.dataset.logType
    this.broadcastServerLogRefresh(logType)
  }

  removeServerLog(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const logType = event.currentTarget.dataset.logType
    this.broadcastServerLogRemoved(logType)
  }

  updateServerLogContextStatus(logType, inContext) {
    const logItem = this.element.querySelector(`[data-log-type="${logType}"]`)
    if (logItem) {
      logItem.dataset.inContext = inContext
      
      if (inContext) {
        logItem.classList.add('bg-success/5', 'border-l-2', 'border-success')
      } else {
        logItem.classList.remove('bg-success/5', 'border-l-2', 'border-success')
      }
    }
  }

  broadcastServerLogContextChange(logType, inContext) {
    this.broadcast("update_server_log_context", {
      log_type: logType,
      in_context: inContext,
      session_id: this.sessionIdValue
    })
  }

  broadcastServerLogRefresh(logType) {
    this.broadcast("refresh_server_log", {
      log_type: logType,
      session_id: this.sessionIdValue
    })
  }

  broadcastServerLogRemoved(logType) {
    this.broadcast("remove_server_log", {
      log_type: logType,
      session_id: this.sessionIdValue
    })
  }

  broadcast(action, data) {
    if (window.App && window.App.cable) {
      window.App.cable.subscriptions.findAll({ identifier: JSON.stringify({ channel: "ClaudeCodeChannel" }) })
        .forEach(subscription => {
          subscription.perform(action, data)
        })
    }
  }

  // Keyboard shortcuts
  keydown(event) {
    // Ctrl/Cmd + F to focus search
    if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
      event.preventDefault()
      if (this.hasSearchInputTarget) {
        this.searchInputTarget.focus()
      }
    }
    
    // Escape to clear search
    if (event.key === 'Escape' && this.hasSearchInputTarget) {
      this.searchInputTarget.value = ""
      this.filterFiles({ target: this.searchInputTarget })
    }
  }

  // Cleanup
  disconnect() {
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.removeEventListener("input", this.filterFiles.bind(this))
    }
  }
}